/**
 * Chart Color Constants for Analytics Dashboard
 *
 * This file defines all colors used in analytics charts to ensure consistency
 * and make it easier to update colors globally. Each color is defined with
 * RGB values and helper functions generate the appropriate format with opacity.
 */

// Base RGB color values for signup funnel stages
export const SIGNUP_FUNNEL_COLORS = {
  codeEntered: [54, 162, 235], // Blue
  emailVerified: [255, 206, 86], // Yellow
  firstLogin: [75, 192, 192], // Teal/Cyan
} as const;

// Base RGB color values for user segments
export const SEGMENT_COLORS = {
  segment1: [255, 99, 132], // Red/Pink
  segment2: [54, 162, 235], // Blue
  segment3: [255, 206, 86], // Yellow
  segment4: [75, 192, 192], // Teal
  segment5: [153, 102, 255], // Purple
  segment6: [255, 159, 64], // Orange
} as const;

/**
 * Helper function to convert RGB array to rgb() string
 */
export const toRgb = (color: readonly number[]): string => {
  return `rgb(${color[0]}, ${color[1]}, ${color[2]})`;
};

/**
 * Helper function to convert RGB array to rgba() string with custom opacity
 */
export const toRgba = (color: readonly number[], alpha: number): string => {
  return `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${alpha})`;
};

/**
 * Pre-defined color sets for signup funnel with different opacities
 */
export const signupFunnelColors = {
  codeEntered: {
    rgb: toRgb(SIGNUP_FUNNEL_COLORS.codeEntered),
    rgbaLight: toRgba(SIGNUP_FUNNEL_COLORS.codeEntered, 0.5),
    rgbaMedium: toRgba(SIGNUP_FUNNEL_COLORS.codeEntered, 0.6),
    rgbaFull: toRgba(SIGNUP_FUNNEL_COLORS.codeEntered, 1),
  },
  emailVerified: {
    rgb: toRgb(SIGNUP_FUNNEL_COLORS.emailVerified),
    rgbaLight: toRgba(SIGNUP_FUNNEL_COLORS.emailVerified, 0.5),
    rgbaMedium: toRgba(SIGNUP_FUNNEL_COLORS.emailVerified, 0.6),
    rgbaFull: toRgba(SIGNUP_FUNNEL_COLORS.emailVerified, 1),
  },
  firstLogin: {
    rgb: toRgb(SIGNUP_FUNNEL_COLORS.firstLogin),
    rgbaLight: toRgba(SIGNUP_FUNNEL_COLORS.firstLogin, 0.5),
    rgbaMedium: toRgba(SIGNUP_FUNNEL_COLORS.firstLogin, 0.6),
    rgbaFull: toRgba(SIGNUP_FUNNEL_COLORS.firstLogin, 1),
  },
} as const;

/**
 * Pre-defined color arrays for segment charts
 */
export const segmentChartColors = {
  backgroundColors: [
    toRgba(SEGMENT_COLORS.segment1, 0.6),
    toRgba(SEGMENT_COLORS.segment2, 0.6),
    toRgba(SEGMENT_COLORS.segment3, 0.6),
    toRgba(SEGMENT_COLORS.segment4, 0.6),
    toRgba(SEGMENT_COLORS.segment5, 0.6),
    toRgba(SEGMENT_COLORS.segment6, 0.6),
  ],
  borderColors: [
    toRgba(SEGMENT_COLORS.segment1, 1),
    toRgba(SEGMENT_COLORS.segment2, 1),
    toRgba(SEGMENT_COLORS.segment3, 1),
    toRgba(SEGMENT_COLORS.segment4, 1),
    toRgba(SEGMENT_COLORS.segment5, 1),
    toRgba(SEGMENT_COLORS.segment6, 1),
  ],
} as const;

/**
 * Default chart point styling
 */
export const DEFAULT_POINT_STYLE = {
  borderWidth: 2,
  pointRadius: 5,
  pointHoverRadius: 7,
  pointBorderColor: "#fff",
  pointBorderWidth: 2,
  pointHoverBackgroundColor: "#fff",
} as const;

/**
 * Default legend configuration
 */
export const DEFAULT_LEGEND_CONFIG = {
  display: true,
  position: "top" as const,
  labels: {
    usePointStyle: true,
    padding: window.innerWidth < 640 ? 10 : 15,
    font: {
      size: window.innerWidth < 640 ? 10 : 12,
    },
    boxWidth: window.innerWidth < 640 ? 8 : 12,
  },
} as const;

/**
 * Default scale configuration for x-axis
 */
export const DEFAULT_X_SCALE_CONFIG = {
  display: true,
  grid: {
    display: true,
    color: "rgba(0, 0, 0, 0.05)",
  },
  ticks: {
    maxRotation: 0,
    minRotation: 0,
    autoSkip: true,
    maxTicksLimit: 15,
    font: {
      size: window.innerWidth < 640 ? 8 : 10,
    },
  },
} as const;

/**
 * Default scale configuration for y-axis
 */
export const DEFAULT_Y_SCALE_CONFIG = {
  beginAtZero: true,
  grid: {
    display: true,
    color: "rgba(0, 0, 0, 0.05)",
  },
  ticks: {
    stepSize: 1,
    font: {
      size: 11,
    },
  },
} as const;
